import { RootState } from '@/store';
import { guildActions, selectJoinModal } from '@/store/features';
import { Form, ReformSubmitHandler, Submit, Textarea } from '@kodkafa/reform';
import React from 'react';
import { connect } from 'react-redux';
import { ModalWrapper } from '../modal';
import { Trans, useTranslation } from 'react-i18next';
import { useClanControllerCreateJoinRequestMutation } from '@/store/services/MidleNestApi.service';

interface JoinRequestModalProps {
  show: boolean;
  guildName?: string | null;
  guildId?: string | null;
  onClose?: () => void;
}

function JoinRequestModal({
  show,
  guildName,
  guildId = null,
  onClose = () => null,
}: JoinRequestModalProps) {
  const { t } = useTranslation();
  const [sendJoinRequest, { isError, error, reset }] = useClanControllerCreateJoinRequestMutation();

  const onSubmit = React.useCallback<ReformSubmitHandler<{ message: string }>>(
    async (data, setError) => {
      if (!guildId) return;

      try {
        await sendJoinRequest({
          clanId: guildId,
          message: data.message,
          requester: 'user',
        }).unwrap();
        onClose();
      } catch (err) {
        setError('name', { message: 'An error occured' });
        return;
      }
    },
    [guildId, sendJoinRequest],
  );

  const closeHandler = React.useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  return (
    <ModalWrapper open={show} auto={true} onClose={closeHandler}>
      <div className='w-full flex flex-col gap-y-8 items-center justify-center'>
        <i className='icon-info text-3xl text-center' />
        <p className='text-center text-sm text-slate-600 max-w-xs dark:text-white'>
          <Trans
            i18nKey='You are sending a request to join <s>{{expr}}</s>. Say something nice!'
            values={{
              expr: guildName,
            }}
            components={{
              s: <strong />,
            }}
          />
        </p>
        <Form onSubmit={onSubmit} className='w-full'>
          <Textarea
            label={t('Request Message')}
            name='message'
            className='w-full !h-32 dark:!bg-transparent dark:!border-[#FFDA68] dark:!text-white dark:ring-transparent'
          />
          {isError && error && (
            <span className='text-sm text-red-500'>
              {((error as any).message as string) ?? 'Error'}
            </span>
          )}
          <div className='flex gap-x-2 justify-center sm:justify-end mt-8'>
            <button
              className='flex items-center justify-center px-4 py-2 text-sm bg-slate-200 rounded-md dark:border bg-transparent dark:border-[#FFDA68] dark:text-[#FFDA68]'
              type='button'
              onClick={onClose}
            >
              {t('Cancel')}
            </button>
            <button
              type='submit'
              className='flex items-center px-6 py-3 text-sm font-bold justify-center whitespace-nowrap bg-blue-500 dark:bg-[#FFDA68] dark:text-[#151515] rounded-md'
            >
              {t('Send Request')}
            </button>
          </div>
        </Form>
      </div>
    </ModalWrapper>
  );
}

export default connect(
  (state: RootState) => ({
    ...selectJoinModal(state),
  }),
  (dispatch) => ({
    onClose: () => dispatch(guildActions.setJoinModal({ show: false, clanId: null })),
  }),
)(JoinRequestModal);
